import React from "react";
import { Alert, Button, Modal } from "antd";
import styled from "styled-components";
import { AudioTrack } from "providers/AudioProvider/types";
import { useAudioStateContext } from "hooks/useAudioStateContext";
import { useAudioHandlesContext } from "hooks/useAudioHandlesContext";
import { useUiLang } from "utils/uiLanguage";
import { formatTimeMilli } from "components/utils";
import { SubtitleBox } from "../SubtitleBox";
import { SubtitleContainerSC } from "../SubtitleBox/styled";
import { CaptionConflictGroup } from "../../captionCollisions";
import { seekOriginalTo } from "utils/originalMedia";
import { announceUserSeek } from "utils/playbackIntent";
import { armVoicingGate } from "utils/playbackHold";

const Columns = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(min(100%, 280px), 1fr));
    gap: 16px;
    margin-top: 8px;
`;

export const CaptionConflictButton = ({ group, onClick }: { group: CaptionConflictGroup; onClick: () => void }) => {
    const en = useUiLang() === "en";
    const preview = group.entries.find(({ track }) => track.translation)?.track.translation || group.entries[0].track.captions;
    return <SubtitleContainerSC full="true" isactive="none" style={{ height: 25, background: "#fffbe6" }}>
        <button type="button" data-caption-conflict={group.key} onMouseDown={(e) => e.stopPropagation()}
            onClick={onClick} title={preview} style={{ height: "100%", width: "100%", border: "1px solid #faad14", borderRadius: 4,
                background: "#fffbe6", color: "#613400", cursor: "pointer", textAlign: "left", overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" }}>
            {en ? `Caption conflict (${group.entries.length}) — review` : `Наложение реплик (${group.entries.length}) — открыть`} · {preview}
        </button>
    </SubtitleContainerSC>;
};

/** The same editors as the timeline, placed in separate normal-flow cards so an empty cue cannot cover another. */
export const CaptionConflictReview = ({ group, onClose, openEditor }: {
    group: CaptionConflictGroup | null;
    onClose: () => void;
    openEditor: (track: AudioTrack) => void;
}) => {
    const state = useAudioStateContext();
    const handles = useAudioHandlesContext();
    const en = useUiLang() === "en";
    if (!group) return null;
    const onlySource = state.lang === state.originalLang;
    const close = () => {
        // Removing a focused contenteditable does not reliably fire blur. Commit its text before closing.
        const focused = document.activeElement as HTMLElement | null;
        if (focused?.isContentEditable && focused.closest("[data-conflict-cue]")) focused.blur();
        onClose();
    };
    return <Modal open title={en ? "Overlapping captions" : "Наложение реплик"} onCancel={close} footer={null}
        width={960} styles={{ body: { maxHeight: "70vh", overflowY: "auto" } }}>
        <Alert type="warning" showIcon message={en
            ? "These captions occupy the same space. Each entry below keeps its own text and timing; overlap alone does not prove duplication."
            : "Эти реплики занимают одно место. Ниже каждая показана отдельно со своим текстом и временем. Наложение не всегда означает дубликат."} />
        {group.entries.map(({ track, index }) => {
            const invalidId = group.duplicateIds.includes(track.id);
            return <section key={`${index}:${track.id}`} data-conflict-cue={track.id}
                style={{ marginTop: 16, padding: 16, background: "#fff", border: "1px solid #d9d9d9", borderRadius: 6 }}>
                <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
                    <strong>{formatTimeMilli(track.start)}–{formatTimeMilli(track.start + Math.max(0, track.length))}</strong>
                    <span style={{ overflowWrap: "anywhere", flex: 1, fontSize: 12 }}>ID: {track.original_id}</span>
                    <Button size="small" disabled={invalidId} onClick={() => {
                        armVoicingGate();
                        seekOriginalTo(track.start);
                        announceUserSeek();
                        handles.setPlayingFragment(null);
                        handles.setPlayStart(track.start);
                        handles.setPlayOriginalStart(track.start);
                        handles.setVoiceoverOnlyPlayback(false);
                        handles.setPlaySavedTrack(false);
                        handles.setPlaying(true);
                    }}>{en ? "Play source" : "Слушать оригинал"}</Button>
                </div>
                {invalidId && <Alert type="error" style={{ marginTop: 8 }} message={en
                    ? "This cue ID occurs more than once. Editing is disabled until the records have distinct IDs."
                    : "Этот ID повторяется. Редактирование недоступно, пока у записей не будет разных ID."} />}
                <Columns>
                    {[false, ...(!onlySource ? [true] : [])].map((right) => <div key={String(right)} style={{ minWidth: 0 }}>
                        <div style={{ marginBottom: 4, fontWeight: 500 }}>{right ? state.lang : state.originalLang}</div>
                        {right && !track.translation && <div style={{ color: "#666", marginBottom: 4 }}>
                            {en ? "No translation yet — enter text below." : "Перевода пока нет — введите текст ниже."}
                        </div>}
                        {invalidId ? <div style={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere", padding: 10 }}>
                            {(right ? track.translation : track.captions) || (en ? "Empty" : "Пусто")}
                        </div> : <SubtitleBox oneTrack={track} zoomLevel={100 - state.zoomLevel} openEditor={openEditor}
                            onTranslate={handles.translateSubtitle} isRightSide={right} inline />}
                    </div>)}
                </Columns>
            </section>;
        })}
    </Modal>;
};
